import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { api } from "../lib/api.ts";
import { useT } from "../lib/i18n.ts";
import { Ic } from "./Icons.tsx";
import { Info, Sheet, Toast, useToast } from "./Ui.tsx";

/* Feedback from anywhere (round 5): a small draggable button on every
   screen opens one box; the current screen travels with the text. */

let openFlag = false;
const listeners = new Set<() => void>();
export const feedback = {
  open() { openFlag = true; listeners.forEach((l) => l()); },
  close() { openFlag = false; listeners.forEach((l) => l()); },
  subscribe(l: () => void) { listeners.add(l); return () => listeners.delete(l); },
  get: () => openFlag,
};

export function FeedbackSheet() {
  const t = useT();
  const open = useSyncExternalStore(feedback.subscribe, feedback.get);
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [body, setBody] = useState("");
  const [toast, say] = useToast();
  if (!open) return <Toast msg={toast} />;
  return (
    <>
      <Sheet title={t("settings.feedback")} onClose={feedback.close} action={<Info text={t("settings.feedbackInfo")} label={t("settings.about")} />}>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            await api.feedback({ category: "other", emailBack: false, body, diagnostics: { route: path, appVersion: "0.1.0", platform: navigator.userAgent.slice(0, 120), locale: navigator.language } });
            feedback.close();
            setBody("");
            say(t("sent"));
          }}
        >
          <div className="field">
            <label htmlFor="fb" className="visually-hidden">{t("settings.feedback")}</label>
            <textarea id="fb" className="input" required autoFocus value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          <button className="btn btn-primary btn-block" type="submit" disabled={!body.trim()}>{t("settings.send")}</button>
          <p className="micro" style={{ marginTop: "var(--sp-3)" }}>{t("feedback.aiNote")}</p>
        </form>
      </Sheet>
      <Toast msg={toast} />
    </>
  );
}

const POS_KEY = "instead.feedback.pos";

type Pos = { x: number; y: number };
const FAB = 44; // the button plus a little margin; the stored spot is kept this far inside the viewport

/** A stored spot is only trusted once it parses as two finite numbers and is pulled back on screen (compat review M4). */
const clamp = (p: Pos): Pos => ({
  x: Math.min(Math.max(4, p.x), window.innerWidth - FAB),
  y: Math.min(Math.max(4, p.y), window.innerHeight - FAB),
});
function storedPos(): Pos | null {
  try {
    const raw = localStorage.getItem(POS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<Pos> | null;
    return p && Number.isFinite(p.x) && Number.isFinite(p.y) ? clamp({ x: p.x!, y: p.y! }) : null;
  } catch { return null; }
}

/**
 * The floating button: drag it anywhere (the spot is remembered on the device);
 * a tap — or Enter / Space — opens the box. Opening happens on `click`, the one
 * event every browser delivers for a tap (Safari drops pointerup after a slight
 * move, feedback F9a); a click that ends a drag is swallowed. While the shelf's
 * select bar is up (`data-bulk` on <html>) CSS lifts the button clear of it.
 */
export function FeedbackButton() {
  const t = useT();
  const ref = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<Pos | null>(storedPos);
  const posRef = useRef(pos);
  const drag = useRef<{ dx: number; dy: number; moved: boolean } | null>(null);
  const swallowClick = useRef(false);

  useEffect(() => {
    const fit = () => setPos((p) => { const next = p ? clamp(p) : p; posRef.current = next; return next; });
    fit(); // the viewport at mount can differ from the one the spot was saved in
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, []);

  return (
    <button ref={ref} type="button" className="fab-feedback" aria-label={t("settings.feedback")} title={t("settings.feedback")}
      data-placed={pos ? "" : undefined}
      style={pos ? ({ "--fab-x": `${pos.x}px`, "--fab-y": `${pos.y}px` } as React.CSSProperties) : undefined}
      onClick={() => {
        if (swallowClick.current) { swallowClick.current = false; return; }
        feedback.open();
      }}
      onPointerDown={(e) => {
        if (e.pointerType === "mouse" && e.button !== 0) return;
        swallowClick.current = false;
        const r = e.currentTarget.getBoundingClientRect();
        drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top, moved: false };
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={(e) => {
        if (!drag.current) return;
        const next = clamp({ x: e.clientX - drag.current.dx, y: e.clientY - drag.current.dy });
        const r = e.currentTarget.getBoundingClientRect();
        if (!drag.current.moved && Math.hypot(next.x - r.left, next.y - r.top) < 6) return;
        drag.current.moved = true;
        posRef.current = next;
        setPos(next);
      }}
      onPointerUp={() => {
        if (!drag.current) return;
        const { moved } = drag.current;
        drag.current = null;
        if (!moved) return; // the click that follows opens the box
        swallowClick.current = true;
        window.setTimeout(() => { swallowClick.current = false; }, 500); // in case no click follows the drag
        try { const p = posRef.current; if (p) localStorage.setItem(POS_KEY, JSON.stringify(p)); } catch { /* fine */ }
      }}
      onPointerCancel={() => { drag.current = null; }}>
      {Ic.bug({ size: 18 })}
    </button>
  );
}
